import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Card from 'react-bootstrap/Card';

// MUI Icons
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import Person2Icon from '@mui/icons-material/Person2';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Custom hooks
import useVideoTime from '../../../hook/useVideoTime';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '../../../data/atoms/utilsAtoms';
import { generatePlayerPageViewUrl, generateTypeOfContentView } from '../../../utils/generatePlayerPageViewUrl';
import { ContentView } from '../../../Pages/VideoPlayer/utils/queryParamsKeys';
import TRC_CONTENT from '@/constants/trcContentType';
import TRC_FILE_TYPE from '@/TRC/constants/trcFileType';
import { LESSON_PLAN_LABELS } from '@/constants/lessonPlanLabels';
import { sendClickEvent, sendConversionEvent } from '@/service/algolia';

const VideoThumbnailCard = ({
	content,
	product,
	customStyles,
	titleStyles,
	savedVideos = [],
	handleSaveUnsave,
	setVideoData,
	modal,
	setModal,
	fld,
	handleDeleteVideo,
	viewType = null,
	subscriptionStatus = true,
	handlePlayerVideo,
	showAddToPlaylist = true,
	showSaveButton = true,
	selectedTags = [],
	selectTagCallback,
	route,
	basePath,
	type = ''
}) => {
	const isTRC = type === TRC_CONTENT.TRC;
	const videoId = content?.videoDetails?._id || content?._id;
	const isLiked =
		content?.videoDetails?.savedvideo ||
		content?.savedvideo ||
		(savedVideos?.length > 0 && savedVideos?.includes(videoId));
	const [isHovered, setIsHovered] = useState(false);
	const userData = useAtomValue(userInfoAtom);
	const navigate = useNavigate();

	// Format content duration
	const formattedDuration = content?.duration ? useVideoTime(content.duration) : '0:00';
	if (
		content?.tagsDetails?.some(tag => tag?.name?.includes('CLASS' || 'CLASSES')) ||
		content?.tags?.some(tag => tag?.name?.includes('CLASS' || 'CLASSES'))
	) {
		viewType = ContentView.CLASSES;
	}
	// Check if content has "coming soon" tag
	const isComingSoon =
		(content?.tagsDetails?.length > 0 &&
			content?.tagsDetails.some(tag => tag?.name?.toLowerCase() === 'coming soon')) ||
		content?.tags?.some(tag => tag?.name?.toLowerCase() === 'coming soon');

	// Determine the correct link based on view type and subscription status
	const getLink = () => {
		if (subscriptionStatus) {
			const typeOfContentView = generateTypeOfContentView(viewType);
			return generatePlayerPageViewUrl(videoId, typeOfContentView, fld?._id);
		}
		return `/plans`;
	};

	// Handle click on card
	const handleCardClick = () => {
		navigate(getLink());
	};

	// Check if user can delete this content
	const canDeleteVideo = () => {
		return (
			(userData.token && (fld?.type === 'PPL' || fld?.type === 'personal-playlist')) ||
			(userData.user?.userType === 'studio-owner' && fld?.type === 'studio-playlist')
		);
	};

	// Handle save/unsave click with preventing propagation
	const handleSaveClick = e => {
		e.stopPropagation();
		handleSaveUnsave(videoId, content);
	};

	// Handle delete with preventing propagation
	const handleDeleteClick = e => {
		e.stopPropagation();
		handleDeleteVideo(e, fld, content);
	};

	// Handle playlist add click with preventing propagation
	const handlePlaylistAdd = e => {
		e.stopPropagation();
		setVideoData(content);
		setModal(!modal);
	};

	const handleTRCVideoClicked = (route, id) => {
		if (basePath) {
			navigate(`/${basePath}/${id}`);
		} else {
			navigate(`/trc/${route}/${id}`);
		}
	};

	const handleVideoClicked = () => {
		if (isTRC && route) {
			if (videoId) {
				sendClickEvent({
					objectID: String(videoId),
					userToken: userData?.user?._id || userData?.user?.userId || 'anonymous'
				});
			}
			handleTRCVideoClicked(route, videoId);
		} else {
			if (viewType !== null) {
				if (videoId) {
					sendClickEvent({
						objectID: String(videoId),
						userToken: userData?.user?._id || userData?.user?.userId || 'anonymous'
					});
				}
				handleCardClick();
			} else {
				handlePlayerVideo(videoId);
			}
		}
	};

	const contentSize = isTRC && content?.fileType === TRC_FILE_TYPE.PDF ? `${content?.pages} pages` : formattedDuration;
	return (
		<Card
			className="bg-dark text-white overflow-hidden m-0"
			style={{
				borderRadius: '20px',
				cursor: isComingSoon ? 'not-allowed' : 'pointer',
				filter: isComingSoon ? 'grayscale(100%)' : 'none',
				pointerEvents: isComingSoon ? 'none' : 'auto',
				minWidth: '350px',
				...customStyles
			}}
			onMouseLeave={() => setIsHovered(false)}
			onMouseEnter={() => setIsHovered(true)}
			onClick={handleVideoClicked}
		>
			<Card.Img
				src={content?.thumnailLink || content?.thumbnailLink || product?.images?.[0]?.src || ''}
				alt={content?.title || product?.title || 'Video thumbnail'}
				style={{ borderRadius: '20px 20px 22px 22px' }}
			/>
			<Card.ImgOverlay
				style={{
					background:
						!isTRC
							? isHovered
								? 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 20%, rgba(0, 0, 0, 0.8) 60%)'
								: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 20%, rgba(0, 0, 0, 0.6) 60%)'
							: 'none',
					transition: 'background 0.3s ease-in-out',
					borderRadius: '20px',
					display: 'flex',
					flexDirection: 'column',
					padding: '0px 6px'
				}}
			>
				<Card.Body className="p-0" style={{ flex: '0 0 auto', height: '0px' }}>
					<Card.Header className="d-flex justify-content-between align-content-center p-0">
						<Tags
							tags={
								content?.categoryDetails?.length
									? content.categoryDetails
									: content?.tagsDetails || content?.tagsData || content?.tags || []
							}
							selectedTags={selectedTags}
							selectTagCallback={selectTagCallback}
							type={type}
						/>
						<div></div>
						<div className="d-flex gap-2 p-1">
							{canDeleteVideo() && (
								<div className="mb-2" onClick={handleDeleteClick}>
									<DeleteOutlineIcon fontSize="large" />
								</div>
							)}
							{showAddToPlaylist && content && userData.token && (
								<div className="mb-0 mb-sm-1" onClick={handlePlaylistAdd}>
									<PlaylistAddIcon fontSize="large" />
								</div>
							)}
						</div>
					</Card.Header>
				</Card.Body>

				<Card.Body
					className="p-1 d-flex flex-column justify-content-between"
					style={{ flex: '1 1 auto', overflow: 'hidden' }}
				>
					<Card.Body
						className="d-flex justify-content-between align-items-end w-100 p-0 mb-0"
						style={{ overflow: 'hidden' }}
					>
						<div
							className="d-flex flex-column align-items-start justify-content-end p-0 w-100"
							style={{
								transform: isHovered ? 'translateY(-10px)' : 'translateY(0)',
								transition: 'transform 0.5s ease-in-out'
							}}
						>
							<div className={`d-flex flex-wrap align-items-center ${isTRC ? 'mb-0' : 'mb-1'}`} style={{ gap: '8px' }}>
								<span
									style={{
										backgroundColor: '#030303',
										padding: '4px 8px',
										borderRadius: '6px',
										fontSize: '1rem'
									}}
									className="content-cardv2-instructor"
								>
									{contentSize}
								</span>

								{/* {content?.isStudioLessonPlan && (
									// <span
									// 	style={{
									// 		backgroundColor: '#097DAC',
									// 		padding: '4px 8px',
									// 		borderRadius: '6px',
									// 		fontSize: '0.75rem',
									// 		fontWeight: '600',
									// 		textTransform: 'uppercase',
									// 		letterSpacing: '0.04em'
									// 	}}
									// 	className="content-cardv2-instructor"
									// >
									// 	{LESSON_PLAN_LABELS.PROGRESSIVE}
									// </span>
								)} */}

								{content?.instructor && (
									<Card.Subtitle className="p-0 m-0">
										<div
											className="d-inline-flex align-items-center content-cardv2-instructor"
											style={{
												fontSize: '1rem',
												fontWeight: '600',
												padding: '4px 8px',
												paddingBottom: '4px',
												borderRadius: '6px',
												gap: '4px',
												color: 'white',
												backgroundColor: '#030303',
												opacity: isHovered ? 1 : 0,
												visibility: isHovered ? 'visible' : 'hidden',
												transition: 'opacity 0.5s ease-in-out, visibility 0.5s ease-in-out'
											}}
										>
											<Person2Icon />
											<strong style={{ paddingTop: '3px' }}>{content?.instructor}</strong>
										</div>
									</Card.Subtitle>
								)}
							</div>
							<Card.Title
								className="mb-0 text-uppercase fw-bolder content-cardv2-title"
								style={{
									fontSize: '1.5rem',
									textAlign: 'start',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									...titleStyles
								}}
							>
								{content?.title || product?.title}
							</Card.Title>

							<Card.Text
								className="mb-0 fw-bolder content-cardv2-description"
								style={{
									maxHeight: isHovered ? '200px' : '0px',
									opacity: isHovered ? 1 : 0,
									overflow: 'hidden',
									transition: 'max-height 0.5s ease-in-out, opacity 0.5s ease-in-out'
								}}
							>
								{content?.shortDescription}
							</Card.Text>
						</div>
						{showSaveButton && userData.token && (
							<div
								onClick={handleSaveClick}
								style={{
									transform: isHovered ? 'translateY(-10px)' : 'translateY(0)',
									transition: 'transform 0.5s ease-in-out'
								}}
							>
								{isLiked ? (
									<FavoriteIcon className="text-danger cursor-pointer" fontSize="large" />
								) : (
									<FavoriteBorderIcon fontSize="large" />
								)}
							</div>
						)}
					</Card.Body>
				</Card.Body>
			</Card.ImgOverlay>
		</Card>
	);
};

// PropTypes for better type checking
VideoThumbnailCard.propTypes = {
	content: PropTypes.object,
	product: PropTypes.object,
	customStyles: PropTypes.object,
	titleStyles: PropTypes.object,
	savedVideos: PropTypes.array,
	handleSaveUnsave: PropTypes.func,
	setVideoData: PropTypes.func,
	modal: PropTypes.bool,
	setModal: PropTypes.func,
	fld: PropTypes.object,
	handleDeleteVideo: PropTypes.func,
	viewType: PropTypes.string,
	subscriptionStatus: PropTypes.bool,
	handlePlayerVideo: PropTypes.func,
	showAddToPlaylist: PropTypes.bool,
	showSaveButton: PropTypes.bool,
	selectedTags: PropTypes.array,
	selectTagCallback: PropTypes.func
};

export default VideoThumbnailCard;

export const Tags = ({ tags, selectedTags, selectTagCallback, type }) => {
	const { pathname } = useLocation();

	// Check if in library view
	let inLibraryView = true;
	if (
		pathname.indexOf('Lessons') === -1 &&
		pathname.indexOf('Exercises') === -1 &&
		pathname.indexOf('Acrossthefloor') === -1 &&
		pathname.indexOf('AllVideos') === -1
	) {
		inLibraryView = false;
	}

	if (!tags || tags.length === 0) return null;

	return (
		<div className="c-video_tag p-1">
			<ul>
				{[...tags]
					.sort((a, b) => (a?.priority ?? 0) - (b?.priority ?? 0))
					.slice(0, 3)
					.map((tag, index) => {
						const tagId = tag?._id || tag?.id || '';
						const url =
							type === TRC_CONTENT.TRC
								? `/trc/view-all?tag=${encodeURIComponent(tagId)}`
								: `/AllVideos?tag=${encodeURIComponent(tagId)}`;

						const tagColor = tag?.color || '#008bc3';
						const isSelected = selectedTags?.includes(tagId);

						return (
							<li
								key={tagId || index}
								className="c-video_tag-entry"
								style={{
									background: tagColor,
									borderColor: isSelected ? 'black' : 'transparent',
									color: isSelected ? 'black' : 'white'
								}}
								onClick={e => {
									e.stopPropagation();
									if (selectTagCallback) {
										selectTagCallback(tag);
									}
								}}
							>
								{!inLibraryView ? (
									<Link to={url} style={{ color: isSelected ? 'black' : 'white' }}>
										{tag?.name}
									</Link>
								) : (
									tag?.name
								)}
							</li>
						);
					})}
			</ul>
		</div>
	);
};

// Update PropTypes for Tags
Tags.propTypes = {
	tags: PropTypes.arrayOf(
		PropTypes.shape({
			_id: PropTypes.string,
			id: PropTypes.string,
			name: PropTypes.string,
			color: PropTypes.string,
			priority: PropTypes.number
		})
	),
	selectedTags: PropTypes.array,
	selectTagCallback: PropTypes.func
};
